// src/components/layout/MenuItem.jsx
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { normalizeMenuUrl } from "../../utils/menuUrl";

const CHEVRON_DOWN = (
  <svg
    className="ml-1.5 h-4 w-4 shrink-0"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const CHEVRON_RIGHT = (
  <svg
    className="ml-auto h-4 w-4 shrink-0 text-mist-400"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const HOVER_CLOSE_DELAY_MS = 120;

const MenuItem = ({ item, level = 0, isGreenNav = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef(null);

  const subItems = Array.isArray(item.sub_menu_items) ? item.sub_menu_items : [];
  const hasSubItems = subItems.length > 0;
  const menuUrl = normalizeMenuUrl(item.url);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const handleOpen = () => {
    clearCloseTimer();
    if (hasSubItems) setIsOpen(true);
  };

  const handleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setIsOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  const handleToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (hasSubItems) setIsOpen((prev) => !prev);
  };

  const greenNavBaseClass =
    "relative inline-flex items-center px-6 2xl:px-7 py-3 text-lg 2xl:text-xl transition-colors duration-150 ease-in-out";
  const greenNavTextColor = "text-white hover:text-white/80";
  const greenNavLinkClass = `${greenNavBaseClass} ${greenNavTextColor}`;
  const greenNavButtonClass = `${greenNavBaseClass} ${greenNavTextColor} focus:outline-none`;

  const topLevelBaseClass =
    "px-3 lg:px-4 py-2 text-base lg:text-xl flex items-center transition-colors duration-150 ease-in-out";
  const topLevelTextColor = "text-gray-700 hover:text-blue-500";
  const topLevelLinkClass = `${topLevelBaseClass} ${topLevelTextColor}`;
  const topLevelButtonClass = `${topLevelBaseClass} ${topLevelTextColor} focus:outline-none`;

  const subMenuPanelClass =
    "absolute w-max min-w-[220px] rounded-md overflow-visible border border-mist-200/80 bg-gradient-to-br from-mist-50 via-white/95 to-lake-50 shadow-xl backdrop-blur-sm";
  const subMenuZIndex = level === 0 ? "z-50" : "z-[70]";
  const subMenuPositionClass =
    level === 0 ? "top-full left-0" : "left-full top-0 -ml-1 pl-1";
  const subMenuItemBaseClass =
    "block w-full text-left px-4 py-3 text-base flex items-center justify-between transition-colors duration-150 ease-in-out text-brand-foreground hover:bg-mist-100/80 hover:text-mist-500";

  const getLinkClass = () => {
    if (level === 0) {
      return isGreenNav ? greenNavLinkClass : topLevelLinkClass;
    }
    return subMenuItemBaseClass;
  };

  const getButtonClass = () => {
    if (level === 0) {
      return isGreenNav ? greenNavButtonClass : topLevelButtonClass;
    }
    return subMenuItemBaseClass;
  };

  const chevronColorClass =
    level === 0 && isGreenNav ? "text-white/70" : "text-gray-500";

  const chevronDown = hasSubItems
    ? React.cloneElement(CHEVRON_DOWN, {
        className: `h-4 w-4 shrink-0 ${chevronColorClass}`,
      })
    : null;

  const renderGreenNavLabel = (title) => (
    <span className="relative inline-block">
      <span className="font-medium">{title}</span>
      {chevronDown && (
        <span
          className="pointer-events-none absolute left-full top-1/2 ml-1.5 inline-flex -translate-y-1/2"
          aria-hidden="true"
        >
          {chevronDown}
        </span>
      )}
    </span>
  );

  const nestedChevron = hasSubItems
    ? React.cloneElement(CHEVRON_RIGHT, {
        className: `ml-2 h-4 w-4 shrink-0 ${chevronColorClass}`,
      })
    : null;

  let trigger = null;

  const useAbsoluteGreenChevron = level === 0 && isGreenNav && hasSubItems;

  if (menuUrl && !hasSubItems) {
    trigger = (
      <Link to={menuUrl} className={getLinkClass()}>
        <span className="font-medium">{item.title}</span>
      </Link>
    );
  } else if (menuUrl && hasSubItems) {
    if (useAbsoluteGreenChevron) {
      trigger = (
        <Link
          to={menuUrl}
          className={getLinkClass()}
          onClick={() => setIsOpen(false)}
          aria-haspopup="menu"
        >
          {renderGreenNavLabel(item.title)}
        </Link>
      );
    } else {
      trigger = (
        <div
          className={`${getButtonClass()} w-full min-w-0 justify-between gap-1`}
        >
          <Link
            to={menuUrl}
            className="font-medium flex-1 min-w-0 text-left truncate"
            onClick={() => setIsOpen(false)}
          >
            {item.title}
          </Link>
          <button
            type="button"
            className="inline-flex shrink-0 items-center rounded p-0.5 focus:outline-none"
            onClick={handleToggleClick}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            aria-label={`Expand ${item.title}`}
          >
            {level > 0 ? nestedChevron : chevronDown}
          </button>
        </div>
      );
    }
  } else if (hasSubItems) {
    trigger = (
      <button
        type="button"
        className={getButtonClass()}
        onClick={useAbsoluteGreenChevron ? undefined : handleToggleClick}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {useAbsoluteGreenChevron ? (
          renderGreenNavLabel(item.title)
        ) : (
          <>
            <span className="font-medium">{item.title}</span>
            {level === 0 ? chevronDown : nestedChevron}
          </>
        )}
      </button>
    );
  } else {
    trigger = (
      <span className={`${getButtonClass()} cursor-default opacity-70`}>
        <span className="font-medium">{item.title}</span>
      </span>
    );
  }

  return (
    <li
      className="relative"
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
    >
      {trigger}
      {hasSubItems && level === 0 && (
        <span
          className="absolute left-0 right-0 top-full h-2"
          aria-hidden="true"
        />
      )}
      {hasSubItems && level > 0 && (
        <span
          className="absolute left-full top-0 h-full w-2"
          aria-hidden="true"
        />
      )}
      {hasSubItems && isOpen && (
        <ul
          className={`${subMenuPanelClass} ${subMenuPositionClass} ${subMenuZIndex}`}
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
        >
          {subItems.map((subItem) => (
            <MenuItem
              key={subItem.id || subItem.title}
              item={subItem}
              level={level + 1}
              isGreenNav={false}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export default MenuItem;
